/**
 * @fileoverview PlannerPuffin Privacy Policy registry data.
 *
 * Hand-migrated faithfully from
 * `apps/planner-web/src/portals/marketing-website/pages/PrivacyPolicyPage.jsx`.
 */

/** @type {import('../../types.js').LegalDocument} */
export const privacy = Object.freeze({
  appKey: 'planner',
  documentKey: 'privacy',
  frontmatter: {
    title: 'Privacy Policy',
    productName: 'PlannerPuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-03-01',
    contactEmail: 'privacy@plannerpuffin.com',
    canonicalUrl: 'https://plannerpuffin.com/privacy',
    ogImage: 'https://plannerpuffin.com/og-image.jpg',
    description:
      'PlannerPuffin privacy policy. Learn how we collect, use, and protect your workforce planning data.',
  },
  sections: [
    {
      id: 'information-we-collect',
      title: '1. Information We Collect',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: 'When you use {productName}, we collect information you provide directly, including your business name, email address, phone number, location data, workforce scheduling data, and sales planning data necessary to provide workforce optimization services.',
        },
        {
          type: 'paragraph',
          text: 'We also automatically collect usage data such as IP addresses, browser type, pages visited, and timestamps to improve our services and ensure security.',
        },
      ],
    },
    {
      id: 'how-we-use-your-information',
      title: '2. How We Use Your Information',
      order: 2,
      blocks: [
        {
          type: 'paragraph',
          text: 'We use your information to provide workforce planning services, generate SPLH analytics, process scheduling data, and communicate planning insights and labor budget recommendations to you.',
        },
        {
          type: 'paragraph',
          text: 'We may also use your information to send service updates, respond to inquiries, improve our scheduling algorithms, and comply with legal obligations.',
        },
      ],
    },
    {
      id: 'data-security',
      title: '3. Data Security',
      order: 3,
      blocks: [
        {
          type: 'paragraph',
          text: 'We implement industry-standard security measures including encryption in transit (TLS) and at rest, access controls, and regular security audits to protect your workforce scheduling data and personal information.',
        },
      ],
    },
    {
      id: 'data-sharing',
      title: '4. Data Sharing',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: 'We do not sell your personal information. We may share data with integration partners solely for the purpose of providing scheduling and payroll connectivity services. We may also share data with service providers who assist in operating our platform under strict confidentiality agreements.',
        },
      ],
    },
    {
      id: 'data-retention',
      title: '5. Data Retention',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: 'We retain your data for as long as your account is active or as needed to provide services. Scheduling and planning data is retained per compliance requirements and for the duration necessary to support historical reporting and analytics.',
        },
      ],
    },
    {
      id: 'your-rights',
      title: '6. Your Rights',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'You have the right to access, correct, or delete your personal information. You may also request a copy of your data or opt out of non-essential communications. To exercise these rights, contact us at the address below.',
        },
      ],
    },
    {
      id: 'cookies-and-analytics',
      title: '7. Cookies and Analytics',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'We use cookies and similar technologies to analyze site traffic and improve your experience. You can manage cookie preferences through our cookie consent banner. Analytics data is collected only with your consent.',
        },
      ],
    },
    {
      id: 'changes-to-this-policy',
      title: '8. Changes to This Policy',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may update this privacy policy from time to time. We will notify you of significant changes by email or through a notice on our website. Continued use of our services after changes constitutes acceptance of the updated policy.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: '9. Contact Us',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about this privacy policy or our data practices, please contact us at {contactEmail}.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
